import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Megaphone, Paperclip, Send, X, Users } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  { id: "solo", l: "Solo" }, { id: "base", l: "Base" }, { id: "pro", l: "Pro" },
  { id: "unlimited", l: "Unlimited" }, { id: "custom", l: "Personalizzato" },
];
const STATUSES = [
  { id: "active", l: "Attive" }, { id: "trialing", l: "In prova" }, { id: "past_due", l: "Pagamento in sospeso" },
];
const MAX_BYTES = 15 * 1024 * 1024;

const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

const CheckGroup = ({ title, items, value, onChange, prefix }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">{title}</div>
    <div className="flex flex-wrap gap-3">
      {items.map((i) => (
        <label key={i.id} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
          <Checkbox checked={value.includes(i.id)} onCheckedChange={() => onChange(toggle(value, i.id))} data-testid={`${prefix}-${i.id}`} className="border-slate-500" />
          {i.l}
        </label>
      ))}
    </div>
  </div>
);

export default function MasterComunicazioni() {
  const [target, setTarget] = useState("agenti");
  const [plans, setPlans] = useState(PLANS.map((p) => p.id));
  const [statuses, setStatuses] = useState(STATUSES.map((s) => s.id));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const params = useCallback(() => ({ target, plans: plans.join(","), statuses: statuses.join(",") }), [target, plans, statuses]);

  useEffect(() => {
    if (!plans.length || !statuses.length) { setCount(0); return; }
    api.get("/master/communications/recipients", { params: params() })
      .then((r) => setCount(r.data.count)).catch(() => setCount(null));
  }, [params, plans.length, statuses.length]);

  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const addFiles = (list) => {
    const next = [...files, ...Array.from(list || [])];
    if (next.reduce((s, f) => s + f.size, 0) > MAX_BYTES) { toast.error("Allegati oltre 15 MB totali"); return; }
    setFiles(next);
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Oggetto e testo sono obbligatori"); return; }
    if (!window.confirm(`Inviare la comunicazione a ${count} destinatari?`)) return;
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries({ ...params(), subject, body }).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/master/communications", fd);
      toast.success(`Invio avviato a ${r.data.recipients} destinatari`);
      setSubject(""); setBody(""); setFiles([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="master-comunicazioni">
      <div>
        <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold">Piattaforma</div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3"><Megaphone className="w-7 h-7 text-amber-400" /> Comunicazioni</h1>
        <p className="text-sm text-slate-400 mt-1">Invia una email a tutte le agenzie con il servizio attivo: nuove funzionalità, manutenzioni, disservizi.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Destinatari</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[{ id: "agenti", l: "Solo agenti titolari", d: "Un'email per ogni agente delle agenzie filtrate" },
              { id: "tutti", l: "Tutte le utenze", d: "Agenti, collaboratori, interni e consultazione attivi" }].map((o) => (
              <button key={o.id} type="button" onClick={() => setTarget(o.id)} data-testid={`comm-target-${o.id}`}
                className={`text-left p-3 rounded-lg border transition-colors ${target === o.id ? "border-amber-500 bg-amber-500/10 text-amber-200" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                <div className="font-semibold text-sm">{o.l}</div>
                <div className="text-xs text-slate-400 mt-0.5">{o.d}</div>
              </button>
            ))}
          </div>
        </div>
        <CheckGroup title="Piano" items={PLANS} value={plans} onChange={setPlans} prefix="comm-plan" />
        <CheckGroup title="Stato agenzia" items={STATUSES} value={statuses} onChange={setStatuses} prefix="comm-status" />
        <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="comm-recipients-count">
          <Users className="w-4 h-4 text-amber-400" />
          Destinatari: <strong className="text-amber-300">{count ?? "—"}</strong>
        </div>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-4">
        <div>
          <Label className="text-slate-300">Oggetto</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-slate-950 border-slate-700 text-slate-100" placeholder="Es. Nuova funzionalità: export Excel dello storico" data-testid="comm-subject" />
        </div>
        <div>
          <Label className="text-slate-300">Messaggio</Label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="bg-slate-950 border-slate-700 text-slate-100" placeholder="Scrivi qui il testo della comunicazione…" data-testid="comm-body" />
        </div>
        <div>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="comm-file-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="border-slate-700 bg-slate-950 text-slate-300 hover:bg-slate-800" data-testid="comm-attach-btn">
            <Paperclip className="w-4 h-4 mr-2" /> Allega file
          </Button>
          <span className="text-xs text-slate-500 ml-3">{(totalBytes / 1024 / 1024).toFixed(2)} / 15 MB</span>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="comm-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between text-sm text-slate-300 bg-slate-950 border border-slate-800 rounded px-3 py-1.5">
                  <span className="truncate">{f.name} <span className="text-xs text-slate-500">({(f.size / 1024).toFixed(0)} KB)</span></span>
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400" data-testid={`comm-file-remove-${i}`}><X className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending || !count} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="comm-send-btn">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Invio…" : `Invia a ${count ?? 0} destinatari`}
          </Button>
        </div>
      </Card>
    </div>
  );
}
